// Client REST type-safe vers l'API MailRegent.
// fetch natif (Node 20 / navigateur). Le token CSRF est lu depuis le cookie
// XSRF-TOKEN et renvoye en header X-XSRF-Token sur toutes les mutations.
//
// P8.G : si une mutation echoue offline (network error / 5xx), on enqueue
// dans le store Dexie outbox pour rejeu via useOutbox() au retour online.

import { outboxDb, type OutboxMethod } from "@/shared/offline/outbox-db";
import { flagMasterKeyMissing } from "@/shared/lib/auth-events";

interface ApiSuccess<T> {
  data: T;
  total?: number;
}
interface ApiError {
  error: { code: string; message: string; field?: string; request_id: string };
}

const QUEUEABLE: OutboxMethod[] = ["POST", "PUT", "PATCH", "DELETE"];

export class OfflineQueuedError extends Error {
  public readonly queuedId: string;
  constructor(queuedId: string) {
    super("Mutation mise en file d'attente offline");
    this.queuedId = queuedId;
    this.name = "OfflineQueuedError";
  }
}

export class ApiCallError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly field?: string | undefined;
  public readonly requestId?: string | undefined;
  constructor(payload: ApiError["error"], status: number) {
    super(payload.message);
    this.code = payload.code;
    this.status = status;
    this.field = payload.field;
    this.requestId = payload.request_id;
  }
}

function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.set(k, String(v));
  }
  const qs = params.toString();
  return qs.length > 0 ? `${path}?${qs}` : path;
}

function buildHeaders(method: string, opts: RequestOptions): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET") {
    const csrf = readCsrfToken();
    if (csrf) headers["X-XSRF-Token"] = csrf;
  }
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  return headers;
}

async function throwFromResponse(res: Response): Promise<never> {
  const text = await res.text();
  let parsed: ApiError | null = null;
  try {
    parsed = JSON.parse(text) as ApiError;
  } catch {
    // ignore
  }
  // v2.5.2 option E : si 401 + message indique masterKey absente, flip le
  // flag global qui ouvre MasterKeyDialog pour re-saisie sans logout.
  if (res.status === 401 && parsed?.error && /master key/i.test(parsed.error.message)) {
    flagMasterKeyMissing();
  }
  if (parsed?.error) throw new ApiCallError(parsed.error, res.status);
  throw new ApiCallError(
    { code: "HTTP_ERROR", message: text || res.statusText, request_id: "" },
    res.status,
  );
}

function isQueueable(method: string, opts: RequestOptions): boolean {
  if (!QUEUEABLE.includes(method as OutboxMethod)) return false;
  if (!opts.idempotencyKey) return false;
  return true;
}

async function queueOffline(
  method: OutboxMethod,
  url: string,
  opts: RequestOptions,
): Promise<never> {
  const id = await outboxDb.add({
    method,
    url,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : null,
    idempotency_key: opts.idempotencyKey ?? crypto.randomUUID(),
  });
  throw new OfflineQueuedError(id);
}

export async function apiCall<T>(path: string, opts: RequestOptions = {}): Promise<ApiSuccess<T>> {
  const method = opts.method ?? "GET";
  const headers = buildHeaders(method, opts);
  const url = buildUrl(path, opts.query);
  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  };
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    if (isQueueable(method, opts)) await queueOffline(method as OutboxMethod, url, opts);
    throw err;
  }
  if (!res.ok) {
    if (res.status >= 500 && isQueueable(method, opts)) {
      await queueOffline(method as OutboxMethod, url, opts);
    }
    await throwFromResponse(res);
  }
  return (await res.json()) as ApiSuccess<T>;
}
